/**
 * HX Ops — Supplier detail tabs.
 *
 * Extends the SupplierDetail modal with:
 *   • Products (grouped by document.category)
 *   • News (manual feed — Option A)
 *   • Meetings (history + follow-ups)
 *   • Notes (timeline of free-form notes)
 *
 * Overview + Documents stay on the parent modal; this component renders
 * only the tab strip + the four extra tab panels.
 */
import React from "react";
import axios from "axios";
import {
  Package, Newspaper, CalendarClock, StickyNote, Plus, Trash2, Loader2,
  ExternalLink, X, AlertCircle, CheckCircle2, Sparkles, Users, ListChecks,
} from "lucide-react";

const P = {
  bg:      "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E4E7EB",
  dim:     "rgba(228,231,235,0.62)",
  accent:  "#3B82F6",
  accent2: "#22C55E",
  silver:  "#C0C7CE",
  amber:   "#F59E0B",
  rose:    "#F43F5E",
};

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

const KIND_COLORS = {
  update:        { color: P.accent,  label: "Update" },
  product:       { color: P.accent2, label: "Product" },
  certification: { color: P.amber,   label: "Certification" },
  financial:     { color: "#A855F7", label: "Financial" },
  regulatory:    { color: P.rose,    label: "Regulatory" },
  company:       { color: P.silver,  label: "Company" },
};

const todayIso = () => new Date().toISOString().slice(0, 10);

// ------------------------------------------------------------------ shared bits

const FieldLabel = ({ children }) => (
  <div className="text-[9.5px] uppercase tracking-[0.24em] mb-1.5" style={{ color: P.silver }}>{children}</div>
);

const Input = React.forwardRef(({ className = "", ...rest }, ref) => (
  <input
    ref={ref}
    className={"w-full px-3 py-2 text-[12.5px] rounded-md outline-none " + className}
    style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${P.border}`, color: P.ink }}
    {...rest}
  />
));
Input.displayName = "Input";

const TextArea = ({ className = "", rows = 3, ...rest }) => (
  <textarea
    rows={rows}
    className={"w-full px-3 py-2 text-[12.5px] rounded-md outline-none resize-y " + className}
    style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${P.border}`, color: P.ink }}
    {...rest}
  />
);

const IconButton = ({ children, onClick, testid, title }) => (
  <button
    onClick={onClick}
    title={title}
    data-testid={testid}
    className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-white/[0.05]"
    style={{ color: P.silver, border: `1px solid ${P.border}` }}
  >
    {children}
  </button>
);

const EmptyState = ({ icon: Icon, title, description, testid }) => (
  <div
    className="rounded-lg p-6 text-center"
    style={{ background: P.surface, border: `1px dashed ${P.border}` }}
    data-testid={testid}
  >
    <Icon size={20} className="mx-auto mb-2" style={{ color: P.silver }} />
    <div className="text-[13px] font-semibold" style={{ color: "#fff" }}>{title}</div>
    <div className="mt-1 text-[11.5px]" style={{ color: P.dim }}>{description}</div>
  </div>
);

// ------------------------------------------------------------------ Products tab

const ProductsPanel = ({ supplier }) => {
  // Group static-supplier documents by category (works with legacy static array),
  // AND merge with backend-indexed documents from `/api/ops/documents?supplier_id=`.
  const [remote, setRemote] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/ops/documents`, { params: { supplier_id: supplier.id } });
        if (mounted) setRemote(r.data || []);
      } catch { /* best-effort */ }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [supplier.id]);

  const grouped = React.useMemo(() => {
    const map = new Map();
    (supplier.documents || []).forEach((d) => {
      const cat = d.type_category || d.size || "Documents";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push({ ...d, source: "static" });
    });
    remote.forEach((d) => {
      const cat = d.category || "Other";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push({ ...d, source: "indexed" });
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [supplier, remote]);

  if (loading) {
    return <div className="flex items-center gap-2 text-[12px]" style={{ color: P.silver }}>
      <Loader2 size={12} className="animate-spin" /> Loading library…
    </div>;
  }
  if (grouped.length === 0) {
    return <EmptyState icon={Package} title="No products yet" description="Documents will appear here grouped by category as they get indexed." testid="supplier-products-empty" />;
  }
  return (
    <div className="space-y-5" data-testid="supplier-products-list">
      {grouped.map(([cat, items]) => (
        <div key={cat}>
          <div className="text-[10px] uppercase tracking-[0.26em] font-semibold mb-2" style={{ color: P.accent }}>
            {cat} <span className="opacity-60">· {items.length}</span>
          </div>
          <div className="space-y-1.5">
            {items.map((it, i) => (
              <div key={i}
                className="flex items-center gap-3 p-2.5 rounded-md"
                style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}` }}
              >
                <Package size={12} style={{ color: P.silver }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] truncate" style={{ color: "#fff" }}>{it.name || it.title || it.original_filename}</div>
                  <div className="text-[10px] mt-0.5" style={{ color: P.dim }}>
                    {it.source === "indexed"
                      ? `Indexed · ${it.pages || 0} pages · ${(it.language || "?").toUpperCase()}`
                      : `${it.type || "PDF"} · ${it.size || ""}`}
                  </div>
                </div>
                <a
                  href={it.source === "indexed" ? `${API}/ops/documents/${it.id}/download` : it.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10.5px] uppercase tracking-wider px-2 py-1 rounded"
                  style={{ background: `${P.accent}18`, color: P.accent, border: `1px solid ${P.accent}44` }}
                >
                  Open
                </a>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ------------------------------------------------------------------ News tab

const NewsPanel = ({ supplier }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({ title: "", date: todayIso(), url: "", summary: "", kind: "update" });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ops/suppliers/${supplier.id}/news`);
      setItems(r.data || []);
    } catch { /* */ }
    finally { setLoading(false); }
  }, [supplier.id]);

  React.useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title.trim() || !form.date) { setError("Title and date are required"); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        title: form.title.trim(),
        date: form.date,
        url: form.url.trim() || null,
        summary: form.summary.trim() || null,
        kind: form.kind,
      };
      await axios.post(`${API}/ops/suppliers/${supplier.id}/news`, payload);
      setForm({ title: "", date: todayIso(), url: "", summary: "", kind: "update" });
      setShowForm(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this news item?")) return;
    try {
      await axios.delete(`${API}/ops/suppliers/${supplier.id}/news/${id}`);
      setItems((x) => x.filter((n) => n.id !== id));
    } catch { /* */ }
  };

  return (
    <div data-testid="supplier-news-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.26em] font-semibold" style={{ color: P.accent }}>
          News & Updates <span className="opacity-60">· {items.length}</span>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="supplier-news-toggle-form"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-md"
          style={{ background: showForm ? P.surface : P.accent, color: showForm ? P.silver : "#fff", border: `1px solid ${showForm ? P.border : "transparent"}` }}
        >
          {showForm ? <><X size={11} /> Cancel</> : <><Plus size={11} /> Add news</>}
        </button>
      </div>

      {showForm && (
        <div className="rounded-lg p-4 mb-4" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <FieldLabel>Title</FieldLabel>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. BENY expands Shanghai plant"
                data-testid="supplier-news-input-title"
              />
            </div>
            <div>
              <FieldLabel>Date</FieldLabel>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="supplier-news-input-date" />
            </div>
            <div>
              <FieldLabel>Kind</FieldLabel>
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                className="w-full px-3 py-2 text-[12.5px] rounded-md outline-none"
                style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${P.border}`, color: P.ink }}
                data-testid="supplier-news-input-kind"
              >
                {Object.entries(KIND_COLORS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <FieldLabel>Source URL (optional)</FieldLabel>
              <Input type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" data-testid="supplier-news-input-url" />
            </div>
            <div className="md:col-span-2">
              <FieldLabel>Summary (optional)</FieldLabel>
              <TextArea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={2} placeholder="One or two lines describing the update" data-testid="supplier-news-input-summary" />
            </div>
          </div>
          {error && (
            <div className="mt-3 text-[11.5px] flex items-center gap-1.5" style={{ color: "#FCA5A5" }} data-testid="supplier-news-error">
              <AlertCircle size={11} /> {error}
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <button
              onClick={submit}
              disabled={saving}
              data-testid="supplier-news-submit"
              className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-semibold rounded-md disabled:opacity-40"
              style={{ background: P.accent, color: "#fff" }}
            >
              {saving ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
              Save
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-[12px]" style={{ color: P.silver }}>
          <Loader2 size={12} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={Newspaper} title="No news yet" description="Add manually or import from a source URL later." testid="supplier-news-empty" />
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const kind = KIND_COLORS[n.kind] || KIND_COLORS.update;
            return (
              <div key={n.id}
                data-testid={`supplier-news-item-${n.id}`}
                className="p-4 rounded-lg" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
                <div className="flex items-start gap-3">
                  <div className="text-center shrink-0 min-w-[54px]">
                    <div className="text-[10px] font-mono uppercase" style={{ color: P.silver }}>
                      {new Date(n.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                    </div>
                    <div className="text-[9px]" style={{ color: P.dim }}>{new Date(n.date).getFullYear()}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ background: `${kind.color}22`, color: kind.color, border: `1px solid ${kind.color}55` }}>
                        {kind.label}
                      </span>
                      <div className="text-[13px] font-semibold" style={{ color: "#fff" }}>{n.title}</div>
                    </div>
                    {n.summary && <div className="mt-1.5 text-[12px]" style={{ color: P.dim }}>{n.summary}</div>}
                    {n.url && (
                      <a href={n.url} target="_blank" rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider"
                        style={{ color: kind.color }}
                        data-testid={`supplier-news-link-${n.id}`}>
                        Source <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                  <IconButton onClick={() => remove(n.id)} testid={`supplier-news-delete-${n.id}`} title="Delete">
                    <Trash2 size={12} />
                  </IconButton>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ Meetings tab

const MeetingsPanel = ({ supplier }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({ title: "", date: todayIso(), attendees: "", agenda: "", minutes: "", follow_ups: "" });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ops/suppliers/${supplier.id}/meetings`);
      setItems(r.data || []);
    } catch { /* */ }
    finally { setLoading(false); }
  }, [supplier.id]);

  React.useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title.trim() || !form.date) { setError("Title and date are required"); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        title: form.title.trim(),
        date: form.date,
        attendees: form.attendees.split(",").map((s) => s.trim()).filter(Boolean),
        agenda: form.agenda.trim() || null,
        minutes: form.minutes.trim() || null,
        follow_ups: form.follow_ups.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      await axios.post(`${API}/ops/suppliers/${supplier.id}/meetings`, payload);
      setForm({ title: "", date: todayIso(), attendees: "", agenda: "", minutes: "", follow_ups: "" });
      setShowForm(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this meeting?")) return;
    try {
      await axios.delete(`${API}/ops/suppliers/${supplier.id}/meetings/${id}`);
      setItems((x) => x.filter((m) => m.id !== id));
    } catch { /* */ }
  };

  return (
    <div data-testid="supplier-meetings-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.26em] font-semibold" style={{ color: P.accent }}>
          Meeting history <span className="opacity-60">· {items.length}</span>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="supplier-meetings-toggle-form"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-md"
          style={{ background: showForm ? P.surface : P.accent, color: showForm ? P.silver : "#fff", border: `1px solid ${showForm ? P.border : "transparent"}` }}
        >
          {showForm ? <><X size={11} /> Cancel</> : <><Plus size={11} /> Log meeting</>}
        </button>
      </div>

      {showForm && (
        <div className="rounded-lg p-4 mb-4" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <FieldLabel>Title</FieldLabel>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Q3 commercial alignment" data-testid="supplier-meetings-input-title" />
            </div>
            <div>
              <FieldLabel>Date</FieldLabel>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="supplier-meetings-input-date" />
            </div>
            <div>
              <FieldLabel>Attendees (comma separated)</FieldLabel>
              <Input value={form.attendees} onChange={(e) => setForm({ ...form, attendees: e.target.value })} placeholder="Rúben, Marco Chen" data-testid="supplier-meetings-input-attendees" />
            </div>
            <div className="md:col-span-2">
              <FieldLabel>Agenda</FieldLabel>
              <TextArea value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} rows={2} data-testid="supplier-meetings-input-agenda" />
            </div>
            <div className="md:col-span-2">
              <FieldLabel>Minutes / Notes</FieldLabel>
              <TextArea value={form.minutes} onChange={(e) => setForm({ ...form, minutes: e.target.value })} rows={4} data-testid="supplier-meetings-input-minutes" />
            </div>
            <div className="md:col-span-2">
              <FieldLabel>Follow-ups (one per line)</FieldLabel>
              <TextArea value={form.follow_ups} onChange={(e) => setForm({ ...form, follow_ups: e.target.value })} rows={3} placeholder={"Send RFQ\nConfirm Incoterms"} data-testid="supplier-meetings-input-followups" />
            </div>
          </div>
          {error && (
            <div className="mt-3 text-[11.5px] flex items-center gap-1.5" style={{ color: "#FCA5A5" }} data-testid="supplier-meetings-error">
              <AlertCircle size={11} /> {error}
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <button
              onClick={submit}
              disabled={saving}
              data-testid="supplier-meetings-submit"
              className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-semibold rounded-md disabled:opacity-40"
              style={{ background: P.accent, color: "#fff" }}
            >
              {saving ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
              Save
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-[12px]" style={{ color: P.silver }}>
          <Loader2 size={12} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={CalendarClock} title="No meetings yet" description="Log your calls, video meets and site visits — build the relationship timeline." testid="supplier-meetings-empty" />
      ) : (
        <div className="space-y-3">
          {items.map((m) => (
            <div key={m.id}
              data-testid={`supplier-meetings-item-${m.id}`}
              className="p-4 rounded-lg" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
              <div className="flex items-start gap-3">
                <div className="text-center shrink-0 min-w-[54px]">
                  <div className="text-[10px] font-mono uppercase" style={{ color: P.silver }}>
                    {new Date(m.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                  </div>
                  <div className="text-[9px]" style={{ color: P.dim }}>{new Date(m.date).getFullYear()}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold" style={{ color: "#fff" }}>{m.title}</div>
                  {m.attendees?.length > 0 && (
                    <div className="mt-1 flex items-center gap-1.5 text-[11px]" style={{ color: P.dim }}>
                      <Users size={10} /> {m.attendees.join(" · ")}
                    </div>
                  )}
                  {m.agenda && (
                    <div className="mt-2">
                      <div className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: P.silver }}>Agenda</div>
                      <div className="text-[12px] whitespace-pre-wrap" style={{ color: P.ink }}>{m.agenda}</div>
                    </div>
                  )}
                  {m.minutes && (
                    <div className="mt-2">
                      <div className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: P.silver }}>Minutes</div>
                      <div className="text-[12px] whitespace-pre-wrap" style={{ color: P.ink }}>{m.minutes}</div>
                    </div>
                  )}
                  {m.follow_ups?.length > 0 && (
                    <div className="mt-2">
                      <div className="text-[9px] uppercase tracking-wider mb-0.5 flex items-center gap-1" style={{ color: P.accent2 }}>
                        <ListChecks size={10} /> Follow-ups
                      </div>
                      <ul className="text-[12px] pl-4 list-disc" style={{ color: P.ink }}>
                        {m.follow_ups.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
                <IconButton onClick={() => remove(m.id)} testid={`supplier-meetings-delete-${m.id}`} title="Delete">
                  <Trash2 size={12} />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ Notes tab

const NotesPanel = ({ supplier }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [content, setContent] = React.useState("");
  const [tags, setTags] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ops/suppliers/${supplier.id}/notes`);
      setItems(r.data || []);
    } catch { /* */ }
    finally { setLoading(false); }
  }, [supplier.id]);

  React.useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!content.trim()) { setError("Note content required"); return; }
    setSaving(true); setError(null);
    try {
      await axios.post(`${API}/ops/suppliers/${supplier.id}/notes`, {
        content: content.trim(),
        tags: tags.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setContent(""); setTags("");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete note?")) return;
    try {
      await axios.delete(`${API}/ops/suppliers/${supplier.id}/notes/${id}`);
      setItems((x) => x.filter((n) => n.id !== id));
    } catch { /* */ }
  };

  return (
    <div data-testid="supplier-notes-panel">
      <div className="text-[10px] uppercase tracking-[0.26em] font-semibold mb-3" style={{ color: P.accent }}>
        Notes <span className="opacity-60">· {items.length}</span>
      </div>
      <div className="rounded-lg p-4 mb-4" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
        <TextArea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Write a quick note about this supplier — pricing quirks, contact preferences, technical caveats…"
          rows={3}
          data-testid="supplier-notes-input-content"
        />
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <Input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="Tags, comma separated (procurement, urgent, technical)"
            className="max-w-md"
            data-testid="supplier-notes-input-tags"
          />
          <button
            onClick={submit}
            disabled={saving || !content.trim()}
            data-testid="supplier-notes-submit"
            className="inline-flex items-center gap-2 px-3.5 py-2 text-[11.5px] font-semibold rounded-md disabled:opacity-40 ml-auto"
            style={{ background: P.accent, color: "#fff" }}
          >
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
            Save note
          </button>
        </div>
        {error && (
          <div className="mt-2 text-[11.5px] flex items-center gap-1.5" style={{ color: "#FCA5A5" }} data-testid="supplier-notes-error">
            <AlertCircle size={11} /> {error}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[12px]" style={{ color: P.silver }}>
          <Loader2 size={12} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={StickyNote} title="No notes yet" description="Notes are private, timestamped and searchable across suppliers (future)." testid="supplier-notes-empty" />
      ) : (
        <div className="space-y-2">
          {items.map((n) => (
            <div key={n.id}
              data-testid={`supplier-notes-item-${n.id}`}
              className="p-3.5 rounded-md" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] whitespace-pre-wrap" style={{ color: P.ink }}>{n.content}</div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {(n.tags || []).map((t) => (
                      <span key={t} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ background: `${P.accent2}18`, color: P.accent2, border: `1px solid ${P.accent2}44` }}>
                        {t}
                      </span>
                    ))}
                    <span className="text-[10px] ml-auto" style={{ color: P.dim }}>
                      {new Date(n.created_at).toLocaleString("pt-PT")}
                    </span>
                  </div>
                </div>
                <IconButton onClick={() => remove(n.id)} testid={`supplier-notes-delete-${n.id}`} title="Delete">
                  <Trash2 size={12} />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ Root tabs

const TAB_DEFS = [
  { id: "products", label: "Products", icon: Package },
  { id: "news",     label: "News",     icon: Newspaper },
  { id: "meetings", label: "Meetings", icon: CalendarClock },
  { id: "notes",    label: "Notes",    icon: StickyNote },
];

export default function SupplierTabs({ supplier }) {
  const [active, setActive] = React.useState("products");
  return (
    <div className="mt-8" data-testid="supplier-tabs">
      <div className="flex items-center gap-1 border-b mb-5 overflow-x-auto" style={{ borderColor: P.border }}>
        {TAB_DEFS.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              data-testid={`supplier-tab-${t.id}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-[12px] font-semibold transition"
              style={{
                color: isActive ? P.accent : P.silver,
                borderBottom: `2px solid ${isActive ? P.accent : "transparent"}`,
                marginBottom: "-1px",
              }}
            >
              <Icon size={13} />
              {t.label}
            </button>
          );
        })}
      </div>
      {active === "products" && <ProductsPanel supplier={supplier} />}
      {active === "news"     && <NewsPanel     supplier={supplier} />}
      {active === "meetings" && <MeetingsPanel supplier={supplier} />}
      {active === "notes"    && <NotesPanel    supplier={supplier} />}
    </div>
  );
}
